/**
 * What the whole job is worth, when the customer bought more than one option.
 *
 * The estimate card's four figures — contract, cost, gross profit, margin —
 * belong to one estimate, because that is what a ladder is: ADR 0030 puts
 * every bucket on the estimate, and two options on one job are two ladders
 * that never touch. That is right, and it is also half an answer. A customer
 * who signs for the roof and the gutters has bought one job, and nothing on
 * the screen added the two up: the rep read 41% on the roof, switched tab,
 * read 28% on the gutters, and had to do the arithmetic themselves to learn
 * what the job they just sold actually makes.
 *
 * Jonathon, 2026-09-23: "when multiple line items are selected and the
 * customer signs for them we need to know these items for the entire job and
 * for each estimate they select."
 *
 * So: per estimate as before, and the sum of them, from the same ladders.
 * Nothing here recomputes anything — it adds up whole `LadderTotals`, so the
 * roll-up cannot disagree with the tabs it is adding (CLAUDE.md rule 1).
 *
 * Which estimates are in it is not this function's question. Every caller
 * passes the job's *active* estimates, which is what `recompute_active_
 * estimates` means by sold: every approved estimate once anything is signed,
 * and the single one being forecast before that. The same set the contract
 * total, the pipeline card and the Sold scoreboard already count.
 */

import type { LadderTotals } from "./ladder.js";
import { addCents, cents, ratio } from "./money.js";

/**
 * The part of a ladder a roll-up reads. A whole `LadderTotals` satisfies it,
 * which is how every caller passes one.
 */
export type RollableLadder = Pick<
  LadderTotals,
  "grandTotal" | "totalCost" | "grossProfit" | "jobOverhead" | "agp"
>;

/** The same four figures as the strip, for every option at once. */
export interface JobLadderTotals extends RollableLadder {
  /** grossProfit / grandTotal, 0 when there is no contract. */
  margin: number;
  /** agp / grandTotal. */
  agpMargin: number;
  /** How many estimates are behind these figures. */
  estimates: number;
}

/**
 * Add up one job's ladders.
 *
 * The margin is taken on the totals rather than averaged across the options,
 * for the reason `scoreboard` gives: a $30k roof at 40% and a $2k gutter run
 * at 20% is not a 30% job. Overhead and AGP add up as well, because each
 * estimate's overhead is a percentage of that estimate's own contract and the
 * job is charged both.
 */
export function combineLadders(
  ladders: readonly RollableLadder[],
): JobLadderTotals {
  const grandTotal = addCents(...ladders.map((l) => l.grandTotal));
  const grossProfit = addCents(...ladders.map((l) => l.grossProfit));
  const agp = addCents(...ladders.map((l) => l.agp));

  return {
    grandTotal,
    totalCost: addCents(...ladders.map((l) => l.totalCost)),
    grossProfit,
    jobOverhead: addCents(...ladders.map((l) => l.jobOverhead)),
    agp,
    margin: ratio(grossProfit, grandTotal),
    agpMargin: ratio(agp, grandTotal),
    estimates: ladders.length,
  };
}

/** Nothing sold yet, so that a caller never has to check for null. */
export const emptyJobLadder = (): JobLadderTotals => ({
  grandTotal: cents(0),
  totalCost: cents(0),
  grossProfit: cents(0),
  jobOverhead: cents(0),
  agp: cents(0),
  margin: 0,
  agpMargin: 0,
  estimates: 0,
});
